import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { EmojiAndColorEditModal } from "@/components/emoji/EmojiAndColorEditModal.tsx";
import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import { TimeOffPoliciesSortModal } from "@/pages/settings/time-off/edit/widgets/TimeOffPoliciesSortModal.tsx";
import {
  useTimeOffCategoryDetails,
  useTimeOffDeleteCategory,
  useTimeOffPolicyDuplicate,
  useTimeOffSaveCategory,
} from "@/queries/use-time-off.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiTimeOffCategoryType,
  ApiTimeOffPaidType,
  ApiTimeOffPeriodTrackingTime,
  getCategoriesTypes,
  getLeaveTypes,
  getPaidTypes,
  getTimeTrackingTypes,
  LeaveTypeRuntimeType,
} from "@/types/time-off/time-off-policy.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Box,
  Button,
  ButtonIcon,
  Card,
  ConfirmationModal,
  DropdownMenuItem,
  DropdownSelect,
  FontAwesomeIcon,
  Input,
  ListItem,
  Page,
  Stack,
  Switch,
  Tag,
  Title,
  toColorName,
  toCssColorValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";

// const limitedColors = ["#1B1D21", "#32309C", "#7124A1", "#AB2C78", "#2F9AB2", "#AFB21D", "#B22F1D"];

export function TimeOffCategoryEdit() {
  const { t } = useTranslation();
  const TimeOffCategoryObject = z.object({
    title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    description: z.string().optional(),
    emoji: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    color: z.string().optional(),
    period_track_type: z.enum(ApiTimeOffPeriodTrackingTime, {
      error: t("errors:field_required"),
    }),
    paid_type: z.enum(ApiTimeOffPaidType, { error: t("errors:field_required") }),
    category_type: z.enum(ApiTimeOffCategoryType, { error: t("errors:field_required") }),
    is_public: z.boolean().optional(),
    is_parental_leave: z.boolean().optional(),
  });
  const { id } = useParams();
  const { openModal } = useModals();
  const isEdit = !!id;
  const { data: timeOffCategory } = useTimeOffCategoryDetails(id ? Number(id) : undefined);
  const {
    handleSubmit,
    control,
    register,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(TimeOffCategoryObject),
    defaultValues: {
      period_track_type: ApiTimeOffPeriodTrackingTime.days,
      emoji: emojiDefaults.time_off.default,
      color: "brand-dark",
      paid_type: ApiTimeOffPaidType.working_paid,
      category_type: ApiTimeOffCategoryType.accrues_time,
      is_public: true,
      is_parental_leave: false,
    },
  });
  const categorySave = useTimeOffSaveCategory(control);
  const categoryDelete = useTimeOffDeleteCategory();
  const policyDuplicate = useTimeOffPolicyDuplicate(id ? Number(id) : undefined);
  const navigate = useNavigate();

  useTitle(timeOffCategory?.title, t("common:edit"));

  const color = watch("color");
  const emoji = watch("emoji");

  useEffect(() => {
    if (!timeOffCategory) return;

    reset({
      title: timeOffCategory.title,
      description: timeOffCategory.description ?? "",
      emoji: timeOffCategory.emoji ?? "",
      color: toColorName(timeOffCategory.color) ?? timeOffCategory.color ?? "brand-dark",
      paid_type: timeOffCategory.paid_type,
      is_public: timeOffCategory.is_public,
      period_track_type: timeOffCategory.period_track_type,
      category_type: timeOffCategory.category_type,
      is_parental_leave: timeOffCategory.is_parental_leave,
    });
  }, [timeOffCategory]);

  const onSubmit = handleSubmit(async (categoryData) => {
    try {
      const createdCategory = await categorySave.mutateAsync({
        id: timeOffCategory?.id ?? 0,
        payload: {
          title: categoryData.title,
          description: categoryData.description ?? "",
          is_public: categoryData.is_public ?? false,
          period_track_type: categoryData.period_track_type,
          paid_type: categoryData.paid_type,
          emoji: categoryData.emoji,
          color: categoryData.color,
          category_type: categoryData.category_type,
          is_parental_leave: categoryData.is_parental_leave ?? false,
        },
      });

      analyticsService.trackEvent(
        isEdit ? analyticEvents.timeOffs.edited : analyticEvents.timeOffs.created,
        {
          [analyticProperties.id]: createdCategory.id,
        }
      );

      if (isEdit) {
        navigate("/settings/time-off");
      } else {
        navigate(`/settings/time-off/categories/${createdCategory.id}`);
      }
    } catch (error) {
      console.log(error);
    }
  });

  const handleDelete = () => {
    if (!timeOffCategory) return;

    const id = timeOffCategory.id;

    openModal(ConfirmationModal, {
      question: t("time_off:category_delete_title"),
      text: t("time_off:category_delete_caption"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        await categoryDelete.mutateAsync(
          { categoryId: id },
          {
            onSuccess: () => {
              analyticsService.trackEvent(analyticEvents.timeOffs.deleted, {
                [analyticProperties.id]: id,
              });

              navigate("/settings/time-off");
            },
          }
        );
      },
    });
  };

  const handleDuplicatePolicy = (policyId: number) => {
    openModal(ConfirmationModal, {
      question: t("time_off:policy_duplicate_title"),
      text: t("time_off:policy_duplicate_caption"),
      acceptTitle: t("common:duplicate"),
      onAccept: async function () {
        await policyDuplicate.mutateAsync(policyId, {
          onSuccess: (copy) => {
            analyticsService.trackEvent(analyticEvents.timeOffs.duplicatedPolicy, {
              [analyticProperties.id]: policyId,
            });
            navigate(`/settings/time-off/categories/${id}/policies/${copy.id}`);
          },
        });
      },
    });
  };

  const handleReorderPolicies = (categoryId: number) => {
    if (!isDirty) {
      openModal(TimeOffPoliciesSortModal, { categoryId });
      return;
    }

    openModal(ConfirmationModal, {
      question: t("common:unsaved_changes_may_be_lost_title"),
      text: t("time_off:policies_order_unsaved_changes"),
      acceptTitle: t("common:continue"),
      secondButtonTitle: t("common:stay"),
      onAccept: async function () {
        openModal(TimeOffPoliciesSortModal, { categoryId });
      },
    });
  };

  const openTakenByUpperEmployees = (employeeIds: number[]) => {
    openModal(PreviewEmployeesModal, {
      filter: { page_number: 1, employee_ids: employeeIds },
      addColumns: [],
      modalTitle: t("core:employees"),
    });
  };

  const changeDecoration = () => {
    openModal(EmojiAndColorEditModal, {
      emoji: emoji ?? "",
      color: toColorName(color) ?? color ?? "brand-dark",
      onSave(emoji: string, color: string) {
        setValue("emoji", emoji);
        setValue("color", color);
      },
    });
  };

  const periodTrackType = watch("period_track_type");
  const isHours = periodTrackType === ApiTimeOffPeriodTrackingTime.hours;

  return (
    <Page>
      <Page.Header
        title={t(isEdit ? "time_off:edit_title" : "time_off:create_new")}
        showBack={true}
        navigateTo={"/settings/time-off"}
      />
      <Page.Content>
        <Stack className={"px-16"} gap={"md"}>
          <Stack direction={"horizontal"} gap={"md"}>
            <Box
              className={
                "relative w-16 h-16 group cursor-pointer rounded-sm items-center justify-center bg-brand-dark/10"
              }
              onClick={changeDecoration}
              style={{ backgroundColor: toCssColorValue(color) }}
            >
              {emoji && <Avatar variant={"plain"} url={undefined} emoji={emoji} size={"xl"} />}
            </Box>
          </Stack>

          <Stack direction={"vertical"}>
            <Input
              required={true}
              label={t("common:title")}
              size={"lg"}
              type={"text"}
              {...register("title")}
              error={errors.title?.message}
            />
            <Input
              label={t("common:description")}
              type={"text"}
              {...register("description")}
              error={errors.description?.message}
            />
          </Stack>

          {isEdit && (
            <Stack direction={"vertical"}>
              <Stack
                direction={"horizontal"}
                gap={"md"}
                items={"center"}
                justify={"between"}
                className={"pt-10 pb-4"}
              >
                <Title
                  header={t("time_off:configure_policies_title")}
                  size={"lg"}
                  caption={t("time_off:policies_order_hint")}
                />
                {timeOffCategory && timeOffCategory.policies.length > 1 && (
                  <Tooltip delay={false}>
                    <TooltipTrigger asChild>
                      <ButtonIcon
                        variant={"secondary"}
                        icon={<FontAwesomeIcon icon={"fa-light fa-arrow-down-arrow-up"} />}
                        onClick={() => handleReorderPolicies(timeOffCategory.id)}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{t("time_off:policies_manage_priority")}</TooltipContent>
                  </Tooltip>
                )}
              </Stack>

              <Stack
                direction={"horizontal"}
                gap={"md"}
                className={"grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"}
              >
                {timeOffCategory?.policies.map((policy, index) => (
                  <Card
                    header={`${index + 1}. ${policy.title}`}
                    caption={
                      policy.is_all_active_employees_only
                        ? t("time_off:policy_fallback_caption")
                        : undefined
                    }
                    key={policy.id}
                    to={`/settings/time-off/categories/${id}/policies/${policy.id}`}
                    menu={
                      <DropdownMenuItem
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          handleDuplicatePolicy(policy.id);
                        }}
                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-copy"} />}
                        title={t("time_off:policy_duplicate_title") + "..."}
                      />
                    }
                    buttonsSlot={
                      <>
                        {policy.employees_count > 0 ? (
                          <Tag>
                            {t("plurals:employees_count", { count: policy.employees_count })}
                          </Tag>
                        ) : (
                          <Tag variant={"warning"}>{t("time_off:policy_no_employees")}</Tag>
                        )}
                        {policy.taken_by_upper_employee_ids.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger
                              className={"flex items-center text-secondary cursor-pointer"}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                openTakenByUpperEmployees(policy.taken_by_upper_employee_ids);
                              }}
                            >
                              <FontAwesomeIcon icon={"fa-light fa-circle-info"} />
                            </TooltipTrigger>
                            <TooltipContent>
                              {t("time_off:policy_taken_by_upper_hint", {
                                count: policy.taken_by_upper_employee_ids.length,
                              })}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </>
                    }
                  />
                ))}
                <Card
                  icon={"fa-plus"}
                  header={t("time_off:create_new_policy_title")}
                  caption={t("time_off:create_new_policy_description")}
                  to={`/settings/time-off/categories/${id}/policies/new`}
                  variant={"action"}
                />
              </Stack>
            </Stack>
          )}

          <Stack direction={"vertical"}>
            <Title
              header={t("time_off:configure_title")}
              paddingTop={true}
              paddingBottom={true}
              size={"lg"}
            />
            <Controller
              render={({ field: { value, onChange } }) => (
                <ListItem
                  layout={"two-columns"}
                  title={t("time_off:period_track_type_title")}
                  valueSlot={
                    <DropdownSelect
                      value={value}
                      onChange={onChange}
                      showCaption={false}
                      options={getTimeTrackingTypes()}
                    />
                  }
                />
              )}
              control={control}
              name={"period_track_type"}
            />
            <Controller
              render={({ field: { value, onChange } }) => (
                <ListItem
                  layout={"two-columns"}
                  title={t("time_off:leave_type")}
                  caption={t("time_off:leave_type_description")}
                  valueSlot={
                    <DropdownSelect
                      value={value ? LeaveTypeRuntimeType.parental : LeaveTypeRuntimeType.regular}
                      onChange={(e) => {
                        onChange(e == LeaveTypeRuntimeType.parental);
                      }}
                      showCaption={false}
                      options={getLeaveTypes()}
                    />
                  }
                />
              )}
              control={control}
              name={"is_parental_leave"}
            />

            <Controller
              render={({ field: { value, onChange } }) => (
                <ListItem
                  layout={"two-columns"}
                  title={t("time_off:category_type_title")}
                  caption={t("time_off:category_type_description", {
                    units: isHours
                      ? t("time_off:hours").toLowerCase()
                      : t("time_off:days").toLowerCase(),
                  })}
                  valueSlot={
                    <DropdownSelect
                      value={value}
                      onChange={onChange}
                      options={getCategoriesTypes()}
                    />
                  }
                />
              )}
              control={control}
              name={"category_type"}
            />

            <Controller
              render={({ field: { value, onChange } }) => (
                <ListItem
                  layout={"two-columns"}
                  title={t("time_off:paid_type_title")}
                  caption={t("time_off:paid_type_description")}
                  valueSlot={
                    <DropdownSelect value={value} onChange={onChange} options={getPaidTypes()} />
                  }
                />
              )}
              control={control}
              name={"paid_type"}
            />
          </Stack>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Switch
                checked={value}
                onChange={(newValue) => {
                  onChange(newValue);
                }}
                size={"sm"}
                title={t("time_off:is_public_title")}
                description={t("time_off:is_public_description")}
              />
            )}
            control={control}
            name={"is_public"}
          />

          {timeOffCategory && (
            <DeleteListItem
              title={t("time_off:category_delete_title")}
              caption={t("time_off:category_delete_caption")}
              onDelete={handleDelete}
            />
          )}

          <Stack className={"pt-(--gap-md)"} gap={"md"}>
            <ServerErrorField errors={errors} />
            <Button
              size={"lg"}
              type={"submit"}
              onClick={onSubmit}
              isLoading={categorySave.isPending}
            >
              {isEdit ? t("common:save") : t("common:save_and_continue")}
            </Button>
          </Stack>
        </Stack>
      </Page.Content>
    </Page>
  );
}
